/* eslint-disable react/prop-types */

import { PlanPrice, PricingPlan } from '../../../types/Pricing';
import { Subscription } from '../../../types/Subscription';
import CheckIcon from '../../../icons/CheckIcon';
import { ToggleButton, ToggleButtonGroup, Card, CardContent, Typography, Box, Button } from '@mui/material';
import redirectToStripe from '../../../utils/stripe';
import { isValidElement, MouseEvent } from 'react';
import { renderIcon } from '../../../landingPage/components/Pricing/PricingCard';

type Props = {
  title: string;
  isActive: boolean;
  price: string;
  type?: 'year' | 'month';
  description?: string
  isHighlighted?: boolean;
  highlights: string[];
  addons: {
    icon: React.ReactNode;
    title: string;
  }[];
  cta: React.ReactNode;
};

interface CustomToggleButtonProps {
  status: string;
  onChange: () => void;
  pricingPlan?: string[];
}

export const CustomToggleButton: React.FC<CustomToggleButtonProps> = ({
  status,
  onChange
}) => {
  return (
    <ToggleButtonGroup
      value={status}
      exclusive
      onChange={onChange}
      aria-label="Billing cycle selection"
      sx={{
        borderRadius: '50px',
        display: 'flex',
        alignItems: 'center',
        boxShadow: 'none'
      }}
    >
      {[
        { value: 'month', label: 'Monthly' },
        { value: 'year', label: 'Yearly' }
      ].map(({ value, label }) => (
        <ToggleButton
          key={value}
          value={value}
          aria-pressed={status === value}
          sx={{
            color: status === value ? '#fff' : '#aaa',
            backgroundColor: status === value ? '#1e40af' : 'transparent',
            borderRadius: '50px',
            textTransform: 'none',
            fontWeight: 500,
            padding: '8px 16px',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: status === value ? '#1e40af' : '#333'
            }
          }}
        >
          {label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

export function PricingTabs({
  plans,
  planPeriod,
  isUserAlreadySubscribed,
  subscription
}: {
  plans: PricingPlan[];
  planPeriod: string;
  isUserAlreadySubscribed: boolean | undefined;
  subscription: Subscription | null
}) {


  const filteredPlans = plans.filter(plan => plan?.prices?.find(price => price?.interval === planPeriod));



  const currentHighlited = ({
    planName,
    activePlanName,
  }: {
    planName: string;
    activePlanName: string;
  }): boolean => {
    const lowerActive = activePlanName.toLowerCase();
    if (
      ['free', 'trial', 'light', 'pro'].includes(lowerActive) &&
      planName === 'PRO'
    ) {
      return true;
    } else if (lowerActive === 'business' && planName === 'BUSINESS') {
      return true;
    } else {
      return false;
    }
  };




  return (
    <div className="flex overflow-x-scroll w-full md:flex-col md:items-center md:overflow-x-scroll lg:flex-row flex-col gap-2 lg:px-10 lg:pr-4 lg:gap-3 md:gap-6 lg:py-3 lg:px-0 lg:justify-center mt-8 h-[60vh] lg:h-auto md:min-h-[30vh] flex-nowrap w-fit lg:pr-[2rem] ">
      {plans.map((plan) => {
        const matchingPrice = plan.prices.find((price) => price.interval === planPeriod);

        if (matchingPrice) {
          const isSamePricing = subscription &&
            matchingPrice.priceId === subscription?.price?.priceId &&
            matchingPrice.amount === subscription?.price?.amount &&
            matchingPrice.interval === subscription?.price?.interval;

          const getButton = (matchingPrice: PlanPrice) => {

            let buttonLabel = 'Purchase Now';
            let onClick = () => redirectToStripe(matchingPrice?.priceId, isUserAlreadySubscribed);

            if (!subscription) {
              buttonLabel = 'Purchase Now';
              onClick = () => redirectToStripe(matchingPrice?.priceId, isUserAlreadySubscribed);
            }
            else if (isSamePricing) {
              buttonLabel = 'Your_Plan';
              onClick = () => redirectToStripe(undefined, subscription.active);
            } else if (matchingPrice.amount < subscription.price.amount) {
              buttonLabel = 'Select';
            } else if (matchingPrice.amount > subscription.price.amount) {
              buttonLabel = 'Select';
            }


            return (
              <>
                {plan?.name.toLowerCase() == 'trial' ? <button onClick={onClick}  >
                  Try
                </button> : buttonLabel != 'Your_Plan' ? <button onClick={onClick}  >
                  Subscribe
                </button> :
                  <button onClick={() => redirectToStripe(matchingPrice?.priceId, true)}  >
                    Manage
                  </button>}
              </>
            );
          };
          return (
            <>
              {
                <PricingCard
                  key={plan?.id}
                  title={plan?.name}
                  price={matchingPrice?.amount}
                  type={matchingPrice?.interval}
                  highlights={plan?.features
                    .slice(0, 3)
                    .map((feature) => feature)}
                  addons={plan?.features?.slice(3).map((feature) => ({
                    title: feature,
                    icon: <CheckIcon className='shrink-0' />
                  }))}
                  isHighlighted={currentHighlited({ planName: plan?.name, activePlanName: subscription?.name })}
                  isActive={isSamePricing ? true : false}
                  description={plan?.description}
                  cta={getButton(matchingPrice)} />
              }
            </>
          );
        }
      })}
      {filteredPlans.length == 0 && <div className='flex w-full  min-h-[38.2vh] items-center justify-center'>
        <p>
          No plan found
        </p>
      </div>}
    </div>
  );
}

export function PricingCard({ title, price, type, highlights, isHighlighted = false, cta, isActive }: Props) {
  const pricingPlan = {
    year: "/year",
    month: "/month"
  };

  let extractedOnClick: ((e: MouseEvent<any>) => void) | undefined;

  if (isValidElement(cta)) {
    const child = cta.props.children;

    if (
      isValidElement(child) &&
      child.props &&
      typeof (child.props as any).onClick === 'function'
    ) {
      extractedOnClick = (child.props as any).onClick;
    } else if (
      cta.props &&
      typeof (cta.props as any).onClick === 'function'
    ) {
      extractedOnClick = (cta.props as any).onClick;
    }
  }

  const planSubtitle = {
    trial: 'Experience Saagas',
    light: 'Discover the possibilities',
    pro: 'Best for frequent creators',
    business: 'Includes priority support'
  };


  const typeOfPlan = pricingPlan[type];
  return (
    <Card
      variant="outlined"
      sx={{
        width: {
          xs: '100%',
          md: '269px',
          maxWidth: '300px',
          marginTop: { xs: '1rem', md: '2rem' },

        },

        position: 'relative',
        mx: { xs: 'auto', md: 0 },
        borderRadius: '1rem',
        border: '1px solid',
        borderColor: isHighlighted ? 'transparent' : '#393939',
        backgroundColor: '#18181B',
        color: '#fff',
        flexShrink: 0,
        transition: 'transform 0.3s',
        '&:hover': {
          md: { transform: 'scale(1.05)' },
        },
        ...(isHighlighted && {
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '1rem',
            padding: '1px',
            background: 'linear-gradient(90deg, #53ADC5 0%, #EA52A1 100%)',
            WebkitMask:
              'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            pointerEvents: 'none',
            zIndex: 1,
          },
          zIndex: 0,
        }),
      }}>
      <CardContent sx={{
        display: 'flex', flexDirection: 'column', gap: { xs: "6px", md: 2 }, width: '269.5px',
        padding: '16px 18px',
        alignSelf: 'center',
        alignItems: 'flex-start',

      }}>

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '9px',
          alignSelf: 'stretch',
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch',
          }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" sx={{
                fontSize: '16.8px', fontWeight: 400, lineHeight: 'normal',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                alignSelf: 'stretch',
              }} gutterBottom>
                { title}

              </Typography>
              {title?.toLowerCase()?.includes('pro') && !isActive && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#0CAECF',
                    fontWeight: 600,
                    fontSize: '12.6px',
                    mt: -0.65,
                  }}
                >
                  Recommended
                </Typography>
              )}
            </Box>

            {isActive && <Box sx={{
              textTransform: 'none',
              alignSelf: 'center',
              height: 20,
              mt: {
                sm: 0,
                md: -1
              },
              backgroundColor: 'background.paper',
              borderRadius: '16px',
              px: {
                xs: 2,
                md: 3
              },
              mx: {
                sm: .5
              },
              display: 'flex',
              padding: '4px 8px',
              border: '1px solid white',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              fontSize: '14px',
              fontWeight: 500,
            }
            } >
              Active
            </Box>}
          </Box>
          <Typography variant="body2" sx={{ fontSize: '14px', color: 'var(--universal-white-text, #FFF)', fontWeight: 400 }} gutterBottom>
            {planSubtitle[title?.toLowerCase()]}
          </Typography>

          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '2rem', mb: 1 }} >
            ${price} {' '}
            <span style={{ fontSize: '16.8px' }}>{typeOfPlan}</span>
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: '8px' }}>


            {highlights.map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                {renderIcon(feature)}
                <Typography variant="body1" color="#A8A8A8" sx={{ fontSize: '14px', fontWeight: 600 }}>
                  {feature}
                </Typography>
              </Box>
            ))}

            {title?.toLowerCase()?.includes('free') &&
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.2rem', opacity: 0 }}>
                {renderIcon('storage')}
                <Typography variant="body1" color="grey.400" sx={{ fontSize: '0.7rem' }}>
                  GB storage
                </Typography>
              </Box>
            }
            {title == 'trial' && <>
              <Typography variant="body1" color="grey.400" sx={{ fontSize: '0.7rem' }}>
              </Typography>
            </>}
          </Box>
        </Box>



        <Box width="100%">
          <Button
            onClick={extractedOnClick}
            variant="contained"
            sx={{
              background: 'linear-gradient(173deg, #0CAECF -29.1%, #580F6B 96.45%)',
              paddingY: 1.5,
              paddingX: 3,
              fontSize: '14px',
              fontWeight: 500,
              height: '32px',
              width: '100%',
              textTransform: 'none',
              borderRadius: '25px',
              color: 'white',
              alignSelf: 'stretch',

              opacity: title.toLowerCase().trim() === 'trial' ? 0.5 : 1,
              pointerEvents: title.toLowerCase().trim() === 'trial' ? 'none' : 'auto',
              '&:hover': {
                background: 'linear-gradient(176.8deg,rgba(12, 175, 207, 0.69) 0%,rgba(89, 15, 107, 0.99) 99% )',
                boxShadow: 'none'
              },
            }}>
            {cta}
          </Button>
        </Box>

      </CardContent>
    </Card >
  );
}